import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
// Закреплённые представления как виджет дашборда.
//
// Это полноценный виджет, а не секция под сеткой: он стоит в общей раскладке,
// двигается стрелками и убирается крестиком в режиме настройки, как остальные.
// Отличие одно — содержимое ведёт пользователь, а не код: внутри виджета
// столько плиток, сколько представлений он закрепил на дашборд.
//
// Пустым виджет тоже показывается. Скрывать его нельзя: исчезнувший с экрана
// виджет невозможно найти в списке «Add widgets», и человек решил бы, что
// закрепление сломалось.
import type { ReactElement } from 'react';
import { Empty, Spin, Typography } from 'antd';
import { LayoutGrid } from 'lucide-react';
import { Link } from 'react-router-dom';

import { WidgetContainer } from '../../../components/dashboard/WidgetContainer.js';
import { ViewRenderer } from './ViewRenderer.js';
import { useSavedViewData, useSavedViews } from '../hooks/useSavedViews.js';
import type { SavedView } from '../api/views.client.js';

const { Text } = Typography;

function SavedViewTile({ view }: { view: SavedView }): ReactElement {
  useI18nSubscription();
  const data = useSavedViewData(view.id);

  return (
    <div
      className="flex flex-col gap-2 crm-radius-m p-3.5"
      style={{
        border: '1px solid var(--ant-color-border-secondary, #e7e5e4)',
        background: 'var(--ant-color-bg-container, #fffdfa)',
      }}
    >
      <Link to={`/views/${view.id}`} style={{ fontWeight: 600 }}>
        {view.title}
      </Link>
      {data.isLoading ? <Spin size="small" /> : null}
      {data.isError ? (
        // Плитка не должна ронять дашборд: у представления могло измениться
        // право на сущность или исчезнуть запись, вокруг которой оно собрано.
        <Text type="secondary">
          {i18n.t(
            'crm.features.features.views.components.dashboardsavedviews.не_удалось_собрать_это_представление',
          )}
        </Text>
      ) : null}
      {data.data ? <ViewRenderer result={data.data} showTitle={false} /> : null}
    </div>
  );
}

export interface DashboardSavedViewsProps {
  testId?: string;
}

export function DashboardSavedViews({ testId }: DashboardSavedViewsProps): ReactElement {
  useI18nSubscription();
  const views = useSavedViews();
  const tiles = (views.data ?? []).filter((view) => view.onDashboard);

  return (
    <WidgetContainer
      title={i18n.t('crm.features.features.views.components.dashboardsavedviews.мои_представления')}
      icon={LayoutGrid}
      minHeight={200}
      {...(testId === undefined ? {} : { testId })}
    >
      {views.isLoading ? <Spin /> : null}
      {!views.isLoading && tiles.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span>
              {i18n.t(
                'crm.features.features.views.components.dashboardsavedviews.пока_ничего_не_закреплено_соберите_вид_в_р',
              )}{' '}
              <Link to="/views">
                {i18n.t('crm.features.features.views.components.dashboardsavedviews.представления')}
              </Link>{' '}
              {i18n.t(
                'crm.features.features.views.components.dashboardsavedviews.и_отметьте_плиткой_на_дашборде',
              )}
            </span>
          }
        />
      ) : null}
      <div className="grid gap-4 xl:grid-cols-2">
        {tiles.map((view) => (
          <SavedViewTile key={view.id} view={view} />
        ))}
      </div>
    </WidgetContainer>
  );
}
